import {
  getBuildVersion,
  getEnvironment,
  getErrorTimeIso,
  toPublicHost,
} from "@/lib/observability/build-info";
// Lazy-loaded to avoid pulling node:async_hooks into the client bundle.
// The observability context is only available on the server.
let _getObservabilityContext:
  | typeof import("@/lib/observability/observability-context").getObservabilityContext
  | undefined;

if (typeof window === "undefined") {
  // Server-only: eagerly resolve the module
  import("@/lib/observability/observability-context").then((mod) => {
    _getObservabilityContext = mod.getObservabilityContext;
  });
}
import type {
  ClientNetworkContext,
  ErrorMetadata,
  ErrorReportContext,
} from "@/lib/observability/types";

function readHeader(request: Request | undefined, key: string): string | null {
  if (!request) return null;
  return request.headers.get(key);
}

export function getUserIpFromRequest(request?: Request): string {
  // Trust chain: cf-connecting-ip (Cloudflare) > x-real-ip (reverse proxy).
  // x-forwarded-for is excluded because it is client-spoofable and would
  // let attackers fabricate IPs in telemetry and security-adjacent contexts
  // (fingerprinting, abuse detection). The rate limiter already excludes it.
  const cf = readHeader(request, "cf-connecting-ip");
  if (cf) return cf;

  const xri = readHeader(request, "x-real-ip");
  if (xri) return xri;

  return "unknown";
}

export function getCountryFromRequest(request?: Request): string {
  return (
    readHeader(request, "cf-ipcountry") ||
    readHeader(request, "x-country-code") ||
    readHeader(request, "x-country") ||
    "unknown"
  );
}

export function getDnsHostFromRequest(request?: Request): string {
  const host =
    readHeader(request, "x-forwarded-host") ||
    readHeader(request, "host") ||
    request?.url ||
    null;
  return toPublicHost(host);
}

export function getUserAgentFromRequest(request?: Request): string {
  return readHeader(request, "user-agent") || "unknown";
}

export function inferBrowserFromUserAgent(userAgent: string): string {
  const ua = userAgent.toLowerCase();

  if (ua.includes("edg/")) return "Edge";
  if (ua.includes("opr/") || ua.includes("opera")) return "Opera";
  if (ua.includes("chrome/") && !ua.includes("edg/")) return "Chrome";
  if (ua.includes("safari/") && !ua.includes("chrome/")) return "Safari";
  if (ua.includes("firefox/")) return "Firefox";
  if (ua.includes("msie") || ua.includes("trident/")) return "IE";

  return "unknown";
}

export function inferDeviceFromUserAgent(userAgent: string): string {
  const ua = userAgent.toLowerCase();

  if (ua.includes("ipad") || ua.includes("tablet")) return "tablet";
  if (
    ua.includes("mobile") ||
    ua.includes("iphone") ||
    ua.includes("android")
  ) {
    return "mobile";
  }

  return "desktop";
}

export function inferOsFromUserAgent(userAgent: string): string {
  const ua = userAgent.toLowerCase();

  // Order matters: check more specific patterns before generic ones
  if (ua.includes("iphone") || ua.includes("ipad") || ua.includes("ipod"))
    return "iOS";
  if (ua.includes("android")) return "Android";
  if (ua.includes("cros")) return "ChromeOS";
  if (ua.includes("windows")) return "Windows";
  if (ua.includes("mac os x") || ua.includes("macintosh")) return "macOS";
  if (ua.includes("linux")) return "Linux";

  return "unknown";
}

/**
 * Infer the client platform from a server-side request.
 * Checks for a custom `X-VidSeeds-Platform` header (set by native/desktop apps),
 * then falls back to "web".
 */
export function inferPlatformFromRequest(request?: Request): string {
  if (!request) return "web";
  const header = request.headers.get("x-vidseeds-platform");
  if (header === "ios" || header === "android" || header === "desktop")
    return `${header}_app`;
  return "web";
}

export function buildServerErrorMetadata(
  context: ErrorReportContext
): ErrorMetadata {
  // Fall back to the AsyncLocalStorage context when the caller didn't
  // pass request/user explicitly (e.g. deep service-level log calls).
  const obsCtx = _getObservabilityContext?.();
  const request = context.request ?? obsCtx?.request;
  const user = context.user ?? obsCtx?.user;
  const userAgent = getUserAgentFromRequest(request);

  return {
    buildVersion: getBuildVersion(),
    environment: getEnvironment(),
    dnsHost: getDnsHostFromRequest(request),
    executionSide: "server",
    errorTimeIso: getErrorTimeIso(),
    userId: user?.id || "anonymous",
    userEmail: user?.email || "anonymous",
    userIp: getUserIpFromRequest(request),
    userBrowser: inferBrowserFromUserAgent(userAgent),
    userDevice: inferDeviceFromUserAgent(userAgent),
    userOs: inferOsFromUserAgent(userAgent),
    userPlatform: inferPlatformFromRequest(request),
    userCountry: getCountryFromRequest(request),
  };
}

export function buildClientErrorMetadata(
  sourceContext: {
    user?: { id?: string; email?: string };
    network?: ClientNetworkContext;
    platform?: string;
  } = {}
): ErrorMetadata {
  const userAgent =
    typeof navigator !== "undefined" ? navigator.userAgent : "unknown";
  const host =
    sourceContext.network?.dnsHost ||
    (typeof window !== "undefined" ? window.location.host : "unknown");

  return {
    buildVersion: getBuildVersion(),
    environment: getEnvironment(),
    dnsHost: host || "unknown",
    executionSide: "client",
    errorTimeIso: getErrorTimeIso(),
    userId: sourceContext.user?.id || "anonymous",
    userEmail: sourceContext.user?.email || "anonymous",
    userIp: sourceContext.network?.ip || "unknown",
    userBrowser: inferBrowserFromUserAgent(userAgent),
    userDevice: inferDeviceFromUserAgent(userAgent),
    userOs: inferOsFromUserAgent(userAgent),
    userPlatform: sourceContext.platform || "web",
    userCountry: sourceContext.network?.country || "unknown",
  };
}
